import {
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay
} from 'rxjs';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { TradingFormState } from '../widgets/trading-form/trading-form.types';
import { generateUniqueId } from '../widgets/chart/utils/chart.util';
import { sanitizeTradingFormState } from './form-state-sanitizer';

export type ChartState = {
  id: string;
  formState: TradingFormState;
};

export type ChartEvent =
  | {
      type: 'ADD_CHART';
      payload: {
        formState: TradingFormState;
        id?: string;
      };
    }
  | {
      type: 'UPDATE_CHART';
      payload: {
        formState: TradingFormState;
        id: string;
      };
    }
  | {
      type: 'DELETE_CHART';
      payload: { id: string };
    }
  | {
      type: 'CLEAR_STATE';
    };

type ChartStateStreams = {
  actions$: Subject<ChartEvent>;
  lastUpdatedChart$: Subject<ChartState>;
  state$: Observable<Map<string, ChartState>>;
};

type ChartStateContextValue = ChartStateStreams;

const ChartStateContext = createContext<ChartStateContextValue | undefined>(
  undefined
);

const createChartStateStreams = (): ChartStateStreams => {
  const actions$ = new Subject<ChartEvent>();
  const lastUpdatedChart$ = new Subject<ChartState>();
  const state$ = actions$.pipe(
    scan((state, event) => {
      const newState = new Map(state);

      switch (event.type) {
        case 'ADD_CHART': {
          const id = event.payload.id || generateUniqueId();
          const sanitizedFormState = sanitizeTradingFormState(
            event.payload.formState,
            { stripFiles: false }
          );
          const chart: ChartState = {
            id,
            formState: sanitizedFormState
          };
          newState.set(id, chart);
          // Emit original payload (with files) for side effects like upload.
          lastUpdatedChart$.next({
            id,
            formState: event.payload.formState
          });
          break;
        }
        case 'UPDATE_CHART': {
          if (newState.has(event.payload.id)) {
            const sanitizedFormState = sanitizeTradingFormState(
              event.payload.formState,
              { stripFiles: false }
            );
            const chart: ChartState = {
              id: event.payload.id,
              formState: sanitizedFormState
            };
            newState.set(event.payload.id, chart);
            // Emit original payload (with files) for side effects like upload.
            lastUpdatedChart$.next({
              id: event.payload.id,
              formState: event.payload.formState
            });
          }
          break;
        }
        case 'DELETE_CHART': {
          newState.delete(event.payload.id);
          break;
        }
        case 'CLEAR_STATE': {
          newState.clear();
          break;
        }
      }
      return newState;
    }, new Map<string, ChartState>()),
    shareReplay(1)
  );

  return {
    actions$,
    lastUpdatedChart$,
    state$
  };
};

export const ChartStateProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const streams = useMemo(() => createChartStateStreams(), []);

  useEffect(() => {
    return () => {
      streams.actions$.complete();
      streams.lastUpdatedChart$.complete();
    };
  }, [streams]);

  return React.createElement(ChartStateContext.Provider, {
    value: streams,
    children
  });
};

export const useChartState = () => {
  const context = useContext(ChartStateContext);
  if (!context) {
    throw new Error('useChartState must be used within ChartStateProvider');
  }

  const { actions$, lastUpdatedChart$, state$ } = context;

  const dispatch = useCallback(
    (event: ChartEvent) => {
      actions$.next(event);
    },
    [actions$]
  );

  const getState$ = useCallback(() => state$, [state$]);

  const getFormStateById$ = useCallback(
    (id: string) => {
      return state$.pipe(
        map((stateMap) => {
          const state = stateMap.get(id);
          return state?.formState;
        }),
        distinctUntilChanged()
      );
    },
    [state$]
  );

  const getChartById$ = useCallback(
    (id: string) => {
      return state$.pipe(
        map((stateMap) => stateMap.get(id)),
        distinctUntilChanged()
      );
    },
    [state$]
  );

  const getLastUpdatedChart$ = useCallback(() => {
    return lastUpdatedChart$.asObservable();
  }, [lastUpdatedChart$]);

  const getActions$ = useCallback(() => {
    return actions$.asObservable();
  }, [actions$]);

  const getAction$ = useCallback(
    <T extends ChartEvent['type']>(type: T) => {
      return getActions$().pipe(
        filter(
          (event): event is Extract<ChartEvent, { type: T }> =>
            event.type === type
        )
      );
    },
    [getActions$]
  );

  return {
    dispatch,
    getState$,
    getChartById$,
    getFormStateById$,
    getLastUpdatedChart$,
    getActions$,
    getAction$
  };
};
